import { fbPixel } from '@websrc/App/Tracking/FacebookPixel'
import { matomo } from '@websrc/App/Tracking/Matomo'
import {
  CoralProductMeta,
  CoralProductMetaState,
  ProductMeta,
  ProductMetaState,
} from '@websrc/Basket/useProductMeta'
import { BasketState } from '@websrc/Typings/DanteBasket'

import { pinterestTrack } from '../Tracking/PinterestPixel'
import { createTrackingProduct, trackEvent, trackLink } from './adobeTracking'
import { priceStringToNumber } from './formatting'
import { isUnderTest } from './isUnderTest'

export const trackAddToCart = (
  eventName: string,
  productMeta: ProductMeta | CoralProductMeta,
  addedQuantity: number,
  discountPrice: string,
  basketState: BasketState
) => {
  if (isUnderTest()) return

  trackLink({
    name: 'Add to Bag Clicked',
    events: 'scAdd',
  })

  const price = priceStringToNumber(discountPrice)

  trackEvent(eventName, createTrackingProduct(productMeta, addedQuantity, discountPrice))

  fbPixel.trackAddToCart(productMeta, price)

  pinterestTrack('AddToCart', { product_id: productMeta.product_number })

  for (const item of basketState.items) {
    matomo.push([
      'addEcommerceItem',
      item.productNumber,
      productMeta.name,
      [(productMeta as ProductMeta).type?.toLocaleLowerCase() || 'unknown'],
      price,
      productMeta.product_number === String(item.productNumber)
        ? item.quantity + addedQuantity
        : item.quantity,
    ])
  }

  const productAlreadyAdded = basketState.items.some(
    (item) => String(item.productNumber) === productMeta.product_number
  )

  if (!productAlreadyAdded) {
    matomo.push([
      'addEcommerceItem',
      productMeta.product_number,
      productMeta.name,
      [(productMeta as ProductMeta).type?.toLocaleLowerCase() || 'unknown'],
      price,
      addedQuantity,
    ])
  }

  matomo.push([
    'trackEcommerceCartUpdate',
    (priceStringToNumber(basketState.subtotal) + price * addedQuantity).toFixed(2),
  ])
}

export const trackRemoveFromCart = (
  eventName: string,
  productMeta: ProductMeta | CoralProductMeta,
  quantityRemaining: number,
  removedQuantity: number,
  discountPrice: string,
  basketState: BasketState
) => {
  if (isUnderTest()) return

  const price = priceStringToNumber(discountPrice)

  trackEvent(eventName, createTrackingProduct(productMeta, removedQuantity, discountPrice))

  for (const item of basketState.items) {
    if (String(item.productNumber) === productMeta.product_number) {
      // Update quantity for changed product, or remove if none left
      if (quantityRemaining === 0) {
        matomo.push(['removeEcommerceItem', productMeta.product_number])
      } else {
        matomo.push([
          'addEcommerceItem',
          productMeta.product_number,
          productMeta.name,
          [(productMeta as ProductMeta).type?.toLocaleLowerCase() || 'unknown'],
          price,
          quantityRemaining,
        ])
      }
    } else {
      // Add all the items still in the basket
      matomo.push([
        'addEcommerceItem',
        item.productNumber,
        productMeta.name,
        [(productMeta as ProductMeta).type?.toLocaleLowerCase() || 'unknown'],
        price,
        item.quantity,
      ])
    }
  }

  matomo.push([
    'trackEcommerceCartUpdate',
    priceStringToNumber(basketState.subtotal) - removedQuantity * price,
  ])
}

export const trackRequestAQuote = (basketState: BasketState) => {
  if (isUnderTest()) return
  matomo.push([
    'trackEvent',
    'eCommerce',
    'RAQ',
    basketState.items.map((item) => `${item.productNumber} - ${item.quantity}`).join(' | '),
    basketState.items
      .map((item) => priceStringToNumber(item.discountPrice ?? item.unitPrice) * item.quantity)
      .reduce((prev, curr) => prev + curr),
  ])

  // We will get an abandoned cart in Matomo, but we make sure that it has 0 revenue left in cart
  matomo.push(['clearEcommerceCart'])
  matomo.push(['trackEcommerceCartUpdate', 0])
}

export const trackSubmitForm = () => {
  if (isUnderTest()) return
  const pageUrl = window.location.href
  if (pageUrl.includes('en-us')) {
    window.gtag?.('event', 'form submission', {
      pagePath: pageUrl,
      send_to: `${process.env.GOOGLE_TAG_ID}/${process.env.GOOGLE_TAG_CONVERSION_LABEL_MARKETING}`,
    })

    window.uetq = window.uetq || ([] as any)
    window.uetq?.push('event', 'submit_lead_form', {})
  }
}

export const trackPurchase = (
  basketState: BasketState,
  productMetaState: ProductMetaState | CoralProductMetaState,
  orderNumber: number
) => {
  if (isUnderTest()) return
  fbPixel.trackPurchase(basketState)

  const basketItemsTotal = basketState.items.reduce(
    (total, item) => total + priceStringToNumber(item.discountPrice ?? item.unitPrice),
    0
  )

  window.gtag?.('event', 'conversion', {
    send_to: `${process.env.GOOGLE_TAG_ID}/${process.env.GOOGLE_TAG_CONVERSION_LABEL_ECOM}`,
    value: basketItemsTotal,
    currency: 'USD',
    transaction_id: String(orderNumber),
  })

  window.uetq = window.uetq || ([] as any)
  window.uetq?.push('event', 'purchase', {
    revenue_value: basketItemsTotal,
    currency: 'USD',
  })

  pinterestTrack('Checkout', {
    value: basketItemsTotal,
    currency: 'USD',
    order_quantity: basketState.items.reduce<number>((prev, curr) => prev + curr.quantity, 0),
    line_items: basketState.items.map((item) => ({
      product_name: productMetaState[item.productNumber]?.name ?? 'unknown',
      product_id: item.productNumber,
      product_price: priceStringToNumber(item.discountPrice ?? item.unitPrice),
      product_quantity: item.quantity,
    })),
  })

  for (const item of basketState.items) {
    const productMeta = productMetaState[item.productNumber]

    if (!productMeta) {
      continue
    }

    matomo.push([
      'addEcommerceItem',
      item.productNumber,
      productMeta.name,
      ['type' in productMeta && productMeta.type ? productMeta.type.toLocaleLowerCase() : ''],
      priceStringToNumber(item.discountPrice ?? item.unitPrice),
      item.quantity,
    ])
  }

  matomo.push([
    'trackEcommerceOrder',
    String(orderNumber),
    priceStringToNumber(basketState.total),
    priceStringToNumber(basketState.subtotal),
    priceStringToNumber(basketState.tax ?? '0'),
    priceStringToNumber(basketState.shipping ?? '0'),
    priceStringToNumber(basketState.discount ?? '0'),
  ])
}
